import React, { Fragment, useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  DialogContentText,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import PropTypes from "prop-types";

import Backdrop from "Components/Common/Backdrop";
import Modal from "Components/Common/Modal";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import {
  useGetBuildingSchedulesQuery,
  usePutGenerateObservationImagesByScheduleIdMutation,
} from "apis/FireSafety/FireSafetyObservation";
import { getImageUrl, isAuthorizedUser } from "utils";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import {
  FIRE_EMERGENCY_CHIEF_FIRE_OFFICER,
  FIRE_EMERGENCY_SR_MANAGER,
  FIRE_SAFETY_SUPER_ADMIN,
} from "common/constant";
import { useSelector } from "react-redux";

const FireSafetyGenerateObservationImages = ({
  handleClose,
  applicationId,
  buildingName,
  t,
}) => {
  const {
    data: { data: buildingSchedulesData } = {},
    isLoading,
    isFetching,
  } = useGetBuildingSchedulesQuery(applicationId);

  const [
    generateComparisonPhotographs,
    { isLoading: isComparisonPhotographGenerating },
  ] = usePutGenerateObservationImagesByScheduleIdMutation();

  const [documentToPreview, setDocumentToPreview] = useState(null);

  const [scheduleId, setScheduleId] = useState("");
  const { roles } = useSelector((state) => state.Login);

  const handleToggle = (id) => {
    if (scheduleId === id) {
      setScheduleId("");
    } else {
      setScheduleId(id);
    }
  };

  useEffect(() => {
    if (buildingSchedulesData?.length === 1) {
      setScheduleId(buildingSchedulesData[0].scheduleId);
    }
  }, [buildingSchedulesData]);

  const handleGenerateImage = async () => {
    try {
      await generateComparisonPhotographs(scheduleId);
    } catch (e) {}
  };

  if (isLoading || isFetching) {
    return <Backdrop open />;
  }

  return (
    <>
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}

      <Modal
        isOpen
        handleClose={handleClose}
        maxWidth="lg"
        title={t("Observation Comparison Photographs") + " - " + buildingName}
      >
        {buildingSchedulesData?.length ? (
          buildingSchedulesData.map(
            ({
              startDate,
              endDate,
              scheduleId: id,
              comparisonPhotographUrl,
            }) => (
              <Fragment key={id}>
                <div
                  className="fs-4 fw-semibold mb-3 border-bottom"
                  onClick={() => handleToggle(id)}
                  key={id}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    className="cursor-pointer"
                  >
                    <div className="d-flex align-items-center gap-1">
                      <p className="fw-bold mb-0">{`${convertUTCDateToLocalDate(
                        startDate
                      ).format("DD-MM-YYYY")} - ${convertUTCDateToLocalDate(
                        endDate
                      ).format("DD-MM-YYYY")}`}</p>
                    </div>
                    <IconButton>
                      {scheduleId === id ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Stack>
                </div>
                <DialogContentText variant="subtitle2" component="div">
                  <Collapse in={scheduleId === id} timeout="auto" unmountOnExit>
                    {isLoading ? (
                      <div className="d-flex justify-content-center">
                        <CircularProgress size={30} />
                      </div>
                    ) : (
                      <Grid item xs={12} md={6} sm={8} lg={6}>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            mb: 1,
                          }}
                        >
                          {isAuthorizedUser(roles, [
                            FIRE_SAFETY_SUPER_ADMIN,
                            FIRE_EMERGENCY_CHIEF_FIRE_OFFICER,
                            FIRE_EMERGENCY_SR_MANAGER,
                          ]) && (
                            <Box sx={{ marginRight: "30px" }}>
                              <Button
                                variant="contained"
                                onClick={handleGenerateImage}
                                disabled={isComparisonPhotographGenerating}
                              >
                                {t("Generate Comparison Photograph")}
                                {isComparisonPhotographGenerating && (
                                  <CircularProgress size={30} />
                                )}
                              </Button>
                            </Box>
                          )}
                          <Box sx={{ marginRight: "30px" }}>
                            <Button
                              variant="outlined"
                              onClick={() =>
                                setDocumentToPreview({
                                  url: getImageUrl(comparisonPhotographUrl),
                                  documentTypeName: "Observation Photographs",
                                  documentType: "pdf",
                                  documentName: "Observation Photographs",
                                })
                              }
                              disabled={!comparisonPhotographUrl}
                            >
                              {t("View Photograph")}
                            </Button>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </Collapse>
                </DialogContentText>
              </Fragment>
            )
          )
        ) : (
          <Typography variant="h6" className="text-center mt-3">
            {t("There is no observations added for this building till now")}
          </Typography>
        )}
      </Modal>
    </>
  );
};

FireSafetyGenerateObservationImages.propTypes = {
  handleClose: PropTypes.func,
  applicationId: PropTypes.number,
  buildingName: PropTypes.string,
  t: PropTypes.func,
};

export default withTranslation()(FireSafetyGenerateObservationImages);
